import { fairSpread, isSharpBook } from "./edge.js";
import { mapStoredGame } from "./storedGame.js";

/**
 * A move smaller than this, in points of de-vigged line, is not a move: the
 * book held our number. A de-vig reads a price skew as a fraction of a point,
 * so a book posting our number at -112/-108 sits a few hundredths off it
 * without having said anything about the game.
 */
export const HELD_THRESHOLD = 0.1;

/** Fewer counted games than this and a book's rate is one lucky week. */
export const MIN_COUNTED_GAMES = 5;

// LowVig is BetOnline's reduced-juice skin (CLAUDE.md, `BOOK_WEIGHTS`), so old
// `betonlineag` rows would score one operator's opinion twice.
const RETIRED_BOOKS = new Set(["betonlineag"]);

function isFinal(game) {
  return (
    game.status === "completed" &&
    Number.isFinite(Number(game.home_score)) &&
    Number.isFinite(Number(game.away_score)) &&
    game.home_score != null &&
    game.away_score != null
  );
}

/** The scorecard's default order: moved-right rate, best first. */
export const DEFAULT_SCORECARD_SORT = { key: "rate", desc: true };

/**
 * Orders scorecard rows by moved-right rate (`key: "rate"`) or avg miss
 * (`key: "miss"`). `desc` reverses the primary column only: ties always go to
 * the book with more games, and thin books always sit last whatever the sort,
 * so one lucky week cannot top the table by being reversed onto it. A row with
 * no value in the sorted column sinks to the bottom of its group either way.
 */
export function sortScorecardRows(rows, sort = DEFAULT_SCORECARD_SORT) {
  const field = sort.key === "miss" ? "avgMiss" : "rate";
  const volume = sort.key === "miss" ? "games" : "counted";
  const direction = sort.desc ? -1 : 1;

  return [...rows].sort((a, b) => {
    const thin = Number(a.thin) - Number(b.thin);
    if (thin) return thin;
    const av = a[field];
    const bv = b[field];
    if (av == null || bv == null) {
      if (av == null && bv != null) return 1;
      if (bv == null && av != null) return -1;
    } else if (av !== bv) {
      return (av - bv) * direction;
    }
    return b[volume] - a[volume] || a.bookmaker.localeCompare(b.bookmaker);
  });
}

/**
 * Each book's track record at the decision deadline, from `/api/v1/history`
 * games (each carrying the books' quotes as of that game's deadline).
 *
 * Everything is home-perspective, like every spread in this app: a home line
 * of -3 is home laying three, and home covers a line L when margin + L > 0.
 *
 * - move: the book's DE-VIGGED deadline line minus the frozen line -- the same
 *   `fairSpread` number the books panel places and shades its dots by
 *   (`favour`), not the posted one. Negative means the book made home more
 *   of a favourite than we did.
 * - held: |move| < HELD_THRESHOLD. Not a call either way, so outside N.
 * - right: the side the book moved toward covered the frozen line. A push on
 *   the frozen line is neither, so it is outside N as well.
 * - miss: |final home margin + book's de-vigged line| -- how far the book's
 *   number was from what happened, over every game the book quoted.
 * - vsFrozen: the book's avg miss minus the frozen line's over that book's own
 *   games, so a book that only quoted the easy games is not flattered.
 *
 * The `baseline` row is the frozen league line's own miss over the same games,
 * so each book can be read against the number we held.
 */
export function bookScorecard(games) {
  const stats = new Map();
  let baselineMiss = 0;
  let gameCount = 0;

  for (const raw of games || []) {
    if (!isFinal(raw)) continue;
    const game = mapStoredGame(raw);
    const frozen = game.fivePicksSpread;
    if (!Number.isFinite(frozen)) continue;

    const margin = Number(raw.home_score) - Number(raw.away_score);
    const frozenCover = margin + frozen; // > 0 home covered, < 0 away covered
    let quoted = false;

    for (const book of game.spreads || []) {
      const key = String(book.bookmaker || "").toLowerCase();
      if (!key || RETIRED_BOOKS.has(key)) continue;
      const fair = fairSpread(book);
      if (fair == null || !Number.isFinite(fair)) continue;
      quoted = true;

      const row = stats.get(key) || {
        bookmaker: key,
        sharp: isSharpBook(key),
        right: 0,
        counted: 0,
        held: 0,
        pushes: 0,
        games: 0,
        missSum: 0,
        frozenMissSum: 0,
      };
      stats.set(key, row);

      row.games += 1;
      row.missSum += Math.abs(margin + fair);
      row.frozenMissSum += Math.abs(frozenCover);

      const move = fair - frozen;
      if (Math.abs(move) < HELD_THRESHOLD) {
        row.held += 1;
      } else if (frozenCover === 0) {
        row.pushes += 1;
      } else {
        row.counted += 1;
        const towardHome = move < 0;
        if (towardHome === frozenCover > 0) row.right += 1;
      }
    }

    if (quoted) {
      gameCount += 1;
      baselineMiss += Math.abs(frozenCover);
    }
  }

  const rows = sortScorecardRows(
    [...stats.values()].map(({ missSum, frozenMissSum, ...row }) => ({
      ...row,
      rate: row.counted ? row.right / row.counted : null,
      avgMiss: row.games ? missSum / row.games : null,
      // Like for like: this book's miss minus the frozen line's miss over the
      // SAME games. Negative means the book landed closer to the result.
      vsFrozen: row.games ? (missSum - frozenMissSum) / row.games : null,
      thin: row.counted < MIN_COUNTED_GAMES,
    }))
  );

  return {
    games: gameCount,
    rows,
    baseline: gameCount ? { games: gameCount, avgMiss: baselineMiss / gameCount } : null,
  };
}
